/**
 * RequestVoiceCallModal — patient-facing call request flow on /dr/{slug}.
 *
 * 1. Patient enters name + mobile → POST /api/voice/request
 * 2. Page polls /call/{id}/status every 2s
 * 3. When status flips to 'accepted' → start WebRTC (caller role)
 * 4. Patient hears doctor; either side can hang up
 */
import React, { useEffect, useRef, useState } from 'react';
import { Phone, PhoneOff, Mic, MicOff, Loader2, X, AlertCircle, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';
import { VoicePeer, bindRemoteAudio } from '@/lib/webrtc';

export default function RequestVoiceCallModal({ slug, doctorName, onClose }) {
  const [step, setStep] = useState('form'); // form | requesting | pending | ringing | accepted | ended | error
  // Structured name parts so the doctor's Patient Log carries the proper
  // full name instead of casual one-word entries.
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [surname, setSurname] = useState('');
  const [mobile, setMobile] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [callId, setCallId] = useState(null);
  const [token, setToken] = useState(null);
  const [iceServers, setIceServers] = useState([]);
  const [muted, setMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [endReason, setEndReason] = useState('');
  const peerRef = useRef(null);
  const pollRef = useRef(null);
  const sigSeqRef = useRef(0);
  const audioRef = useRef(null);
  const startTimeRef = useRef(null);
  const tickRef = useRef(null);

  const stopAll = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
    pollRef.current = null;
    tickRef.current = null;
    if (peerRef.current) {
      try { peerRef.current.close(); } catch {}
      peerRef.current = null;
    }
  };

  useEffect(() => {
    return () => stopAll();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const trimFirst = (firstName || '').trim();
    const trimMid = (middleName || '').trim();
    const trimSur = (surname || '').trim();
    if (trimFirst.length < 2 || mobile.trim().length < 4) {
      setError('First name (min 2 letters) and mobile are required');
      return;
    }
    const composedFull = [trimFirst, trimMid, trimSur].filter(Boolean).join(' ');
    setStep('requesting'); setError('');
    try {
      const r = await api.post('/voice/request', {
        slug,
        patient_name: composedFull,
        first_name: trimFirst,
        middle_name: trimMid,
        surname: trimSur,
        patient_mobile: mobile.trim(),
        note: note.trim() || null,
      });
      setCallId(r.data.call_id);
      setToken(r.data.patient_token);
      setIceServers(r.data.ice_servers || []);
      setStep(r.data.status === 'pending' ? 'pending' : 'ringing');
      startStatusPolling(r.data.call_id, r.data.patient_token, r.data.ice_servers);
    } catch (err) {
      setStep('error');
      setError(err?.response?.data?.detail || 'Could not initiate call');
    }
  };

  const startStatusPolling = (id, tok, servers) => {
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/voice/call/${id}/status`, { params: { token: tok } });
        if (r.data.status === 'accepted' && step !== 'accepted') {
          clearInterval(pollRef.current); pollRef.current = null;
          await startCall(id, tok, servers);
        } else if (r.data.status === 'ringing' && step === 'pending') {
          setStep('ringing');
        } else if (['rejected', 'ended', 'expired'].includes(r.data.status)) {
          setEndReason(r.data.status);
          setStep('ended');
          stopAll();
        }
      } catch {}
    }, 2000);
  };

  const startCall = async (id, tok, servers) => {
    setStep('accepted');
    startTimeRef.current = Date.now();
    tickRef.current = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    const peer = new VoicePeer({
      role: 'caller',
      iceServers: servers,
      onState: (s) => {
        if (s === 'failed' || s === 'closed') {
          setEndReason('connection_failed');
          setStep('ended');
          stopAll();
        }
      },
      onRemoteStream: (stream) => bindRemoteAudio(audioRef.current, stream),
      onError: (msg) => setError(msg),
      onLocalSignal: async ({ type, payload }) => {
        try { await api.post(`/voice/call/${id}/signal`, { type, payload, token: tok }); } catch {}
      },
    });
    peerRef.current = peer;
    try {
      await peer.start();
    } catch {
      setEndReason('mic_denied');
      setStep('ended');
      stopAll();
      return;
    }
    // Start signal polling (1.5s during setup, slows after connected)
    let interval = 1500;
    const sigPoll = async () => {
      if (!peerRef.current) return;
      try {
        const r = await api.get(`/voice/call/${id}/signal`, { params: { since: sigSeqRef.current, token: tok } });
        for (const s of r.data.signals || []) {
          sigSeqRef.current = Math.max(sigSeqRef.current, s.seq);
          await peer.receiveSignal({ type: s.type, payload: s.payload });
        }
        if (peer.pc?.connectionState === 'connected') interval = 5000;
      } catch {}
    };
    pollRef.current = setInterval(sigPoll, interval);
  };

  const hangup = async () => {
    if (callId && token) {
      try { await api.post(`/voice/call/${callId}/signal`, { type: 'hangup', payload: {}, token }); } catch {}
    }
    setEndReason('patient_hangup');
    setStep('ended');
    stopAll();
  };

  const dismiss = () => { stopAll(); onClose && onClose(); };

  const fmt = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="voice-call-modal">
      <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl overflow-hidden">
        <audio ref={audioRef} autoPlay playsInline />
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone size={16} className="text-emerald-600" />
            <p className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Voice call · {doctorName}
            </p>
          </div>
          <button onClick={dismiss} className="text-slate-400 hover:text-slate-700" data-testid="voice-call-close">
            <X size={18} />
          </button>
        </div>

        {step === 'form' && (
          <form onSubmit={submit} className="p-6 space-y-4">
            <p className="text-sm text-slate-600">Your number stays private — connection is direct browser-to-browser.</p>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1.5">
                First name <span className="text-rose-600">*</span>
              </label>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={40}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none text-sm"
                placeholder="e.g. Aarav" data-testid="voice-call-first-name" required />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1.5">Middle name <span className="text-slate-400 font-normal">(optional)</span></label>
                <input value={middleName} onChange={(e) => setMiddleName(e.target.value)} maxLength={40}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none text-sm"
                  placeholder="e.g. Raj" data-testid="voice-call-middle-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 block mb-1.5">Surname <span className="text-amber-600 font-medium">(please share)</span></label>
                <input value={surname} onChange={(e) => setSurname(e.target.value)} maxLength={40}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none text-sm"
                  placeholder="e.g. Patel" data-testid="voice-call-surname" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1.5">Mobile (for callback if disconnected)</label>
              <input value={mobile} onChange={(e) => setMobile(e.target.value)} maxLength={20}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none text-sm"
                placeholder="+91 98xxxxxxxx" data-testid="voice-call-mobile" required inputMode="tel" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1.5">Reason (optional)</label>
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200}
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none text-sm"
                placeholder="Quick follow-up about prescription" data-testid="voice-call-note" />
            </div>
            {error && <p className="text-xs text-red-600 flex items-center gap-1.5"><AlertCircle size={12} />{error}</p>}
            <button type="submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-xl text-base font-semibold hover:bg-emerald-700 transition-colors"
              data-testid="voice-call-submit">
              <Phone size={16} />Request voice call
            </button>
            <p className="text-[10px] text-slate-400 text-center">By calling you agree to be voice-recorded only if the doctor explicitly enables recording for clinical safety. Default: no recording.</p>
          </form>
        )}

        {step === 'requesting' && (
          <div className="p-10 text-center">
            <Loader2 size={28} className="animate-spin text-sky-600 mx-auto mb-3" />
            <p className="text-sm text-slate-600">Connecting…</p>
          </div>
        )}

        {step === 'pending' && (
          <div className="p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-50 mx-auto flex items-center justify-center mb-4 animate-pulse">
              <Phone size={24} className="text-amber-600" />
            </div>
            <p className="text-base font-semibold text-slate-900 mb-1.5">Waiting for {doctorName} to approve…</p>
            <p className="text-xs text-slate-500 mb-5">Manual mode — they'll be notified instantly.</p>
            <button onClick={hangup} className="text-xs text-slate-500 hover:text-slate-700 underline" data-testid="voice-call-cancel">Cancel</button>
          </div>
        )}

        {step === 'ringing' && (
          <div className="p-10 text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-50 mx-auto flex items-center justify-center mb-4">
              <Phone size={28} className="text-emerald-600 animate-bounce" />
            </div>
            <p className="text-base font-semibold text-slate-900 mb-1.5">Ringing {doctorName}…</p>
            <p className="text-xs text-slate-500 mb-5">Please allow microphone access when prompted.</p>
            <button onClick={hangup}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-rose-600 text-white rounded-xl text-sm font-medium hover:bg-rose-700"
              data-testid="voice-call-hangup">
              <PhoneOff size={14} />Cancel
            </button>
          </div>
        )}

        {step === 'accepted' && (
          <div className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
              <CheckCircle2 size={32} className="text-emerald-700" />
            </div>
            <p className="text-base font-semibold text-slate-900 mb-1">Connected · {doctorName}</p>
            <p className="text-xs text-emerald-700 font-mono mb-6" data-testid="voice-call-duration">{fmt(callDuration)}</p>
            <div className="flex justify-center gap-3">
              <button onClick={() => { setMuted(!muted); peerRef.current?.setMicMuted(!muted); }}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium ${muted ? 'bg-slate-200 text-slate-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                data-testid="voice-call-mute">
                {muted ? <MicOff size={14} /> : <Mic size={14} />}{muted ? 'Unmute' : 'Mute'}
              </button>
              <button onClick={hangup}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-rose-600 text-white rounded-xl text-sm font-medium hover:bg-rose-700"
                data-testid="voice-call-end">
                <PhoneOff size={14} />End call
              </button>
            </div>
            {error && <p className="text-[11px] text-amber-600 mt-3">{error}</p>}
          </div>
        )}

        {step === 'ended' && (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 mx-auto flex items-center justify-center mb-4">
              <PhoneOff size={24} className="text-slate-500" />
            </div>
            <p className="text-base font-semibold text-slate-900 mb-1">Call ended</p>
            <p className="text-xs text-slate-500 mb-1">
              {endReason === 'rejected' && 'The doctor was unable to take the call.'}
              {endReason === 'expired' && 'No answer.'}
              {endReason === 'mic_denied' && 'Microphone access was denied.'}
              {endReason === 'patient_hangup' && 'You ended the call.'}
              {endReason === 'connection_failed' && 'Connection failed. Please try again.'}
              {!['rejected', 'expired', 'mic_denied', 'patient_hangup', 'connection_failed'].includes(endReason) && 'Call ended.'}
            </p>
            {callDuration > 0 && <p className="text-[11px] text-slate-400 mt-1">Duration: {fmt(callDuration)}</p>}
            <button onClick={dismiss} className="mt-5 px-5 py-2.5 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800" data-testid="voice-call-dismiss">Close</button>
          </div>
        )}

        {step === 'error' && (
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-rose-50 mx-auto flex items-center justify-center mb-4">
              <AlertCircle size={22} className="text-rose-600" />
            </div>
            <p className="text-sm font-semibold text-slate-900 mb-1.5">Could not start call</p>
            <p className="text-xs text-slate-500 mb-5">{error}</p>
            <button onClick={dismiss} className="px-5 py-2.5 bg-slate-900 text-white rounded-xl text-sm font-medium" data-testid="voice-call-error-close">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
